import React, { useState } from "react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function strength(pw) {
  return {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function SignInMethods() {
  const { user, refresh } = useAuth();
  const hasPassword = !!user?.has_password;

  // password modal state
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const s = strength(newPw);
  const pwValid = s.length && s.upper && s.digit && s.symbol;

  const submitPw = async () => {
    setPwLoading(true);
    try {
      await api.post("/auth/set-password", {
        current_password: hasPassword ? currentPw : undefined,
        new_password: newPw,
      });
      await refresh();
      toast.success(hasPassword ? "Password updated." : "Password set. You can now sign in with email & password.");
      setPwOpen(false);
      setCurrentPw(""); setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update password.");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid="sign-in-methods">
      <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-2">Sign-in methods</h2>
      <p className="text-sm text-[#555568] mb-5">
        WYLO uses email & password for sign-in.
      </p>

      <div className="space-y-3">
        {/* Email + password */}
        <div className="flex items-center justify-between gap-3 rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center">
              <Mail size={16} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1A1A2E]">Email & password</p>
              <p className="text-xs text-[#555568] font-mono">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasPassword ? (
              <span className="text-xs uppercase tracking-[0.18em] text-[#1D9E75] font-semibold inline-flex items-center gap-1" data-testid="password-status-active">
                <Check size={12} /> Active
              </span>
            ) : (
              <span className="text-xs uppercase tracking-[0.18em] text-[#BA7517] font-semibold" data-testid="password-status-inactive">
                Not set
              </span>
            )}
            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="open-password-dialog">
                  {hasPassword ? "Change" : "Set password"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{hasPassword ? "Change password" : "Set a password"}</DialogTitle>
                  <DialogDescription>
                    {hasPassword
                      ? "Enter your current password, then a new one."
                      : "Set a password so you can sign in with email."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  {hasPassword && (
                    <div>
                      <Label>Current password</Label>
                      <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} data-testid="password-current-input" />
                    </div>
                  )}
                  <div>
                    <Label>New password</Label>
                    <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="password-new-input" />
                    <ul className="mt-2 grid grid-cols-2 gap-1 text-xs">
                      <Req ok={s.length} label="≥ 12 characters" />
                      <Req ok={s.upper} label="1 uppercase" />
                      <Req ok={s.digit} label="1 number" />
                      <Req ok={s.symbol} label="1 symbol" />
                    </ul>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
                  <Button
                    onClick={submitPw}
                    disabled={!pwValid || pwLoading || (hasPassword && !currentPw)}
                    className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white"
                    data-testid="password-submit-btn"
                  >
                    {pwLoading ? "Saving…" : hasPassword ? "Update password" : "Set password"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
}

function Req({ ok, label }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-[#1D9E75]" : "text-[#555568]"}`}>
      <Check size={12} style={{ opacity: ok ? 1 : 0.3 }} />
      {label}
    </li>
  );
}
